// Types for the Securities Research suite: volatility screener, idea tracker,
// filings browser, and the Opus-powered research assistant.

// ─── Screener ────────────────────────────────────────────────

export const SECTORS = [
  'Technology',
  'Communication Services',
  'Consumer Discretionary',
  'Consumer Staples',
  'Energy',
  'Financials',
  'Healthcare',
  'Industrials',
  'Materials',
  'Real Estate',
  'Utilities',
] as const

export type Sector = (typeof SECTORS)[number]

/** Short-term volatility snapshot for one equity. Vols are annualized percents. */
export interface VolatilityProfile {
  symbol: string
  name: string
  sector: Sector
  price: number
  marketCap: number
  /** 30-day realized (historical) volatility, annualized % */
  realizedVol30d: number
  /** ~30-day at-the-money implied volatility, annualized % */
  impliedVol30d: number
  /** ~7-day ATM implied volatility, annualized % */
  impliedVol7d: number | null
  /** impliedVol30d − realizedVol30d, in vol points. Positive = options rich vs realized. */
  volDivergence: number
  /** impliedVol30d / realizedVol30d */
  divergenceRatio: number
  /** Where today's 30d IV sits in its 52-week range, 0–100 */
  ivPercentile52w: number | null
  nextEarningsDate: string | null
  daysUntilEarnings: number | null
}

export type DivergenceDirection = 'any' | 'rich' | 'cheap'

export interface ScreenerCriteria {
  /** Empty/undefined = all sectors */
  sectors?: string[]
  /** Minimum |implied − realized| in vol points */
  minDivergence?: number
  /** rich = implied > realized, cheap = implied < realized */
  direction?: DivergenceDirection
  minMarketCap?: number
  sortBy?: 'divergence' | 'ratio' | 'impliedVol' | 'marketCap' | 'earnings'
  /** When true, results are grouped and ranked within each sector */
  groupBySector?: boolean
  limit?: number
}

export interface ScreenerResult {
  asOf: string
  criteria: ScreenerCriteria
  results: VolatilityProfile[]
  /** Present when criteria.groupBySector — top divergent names per sector */
  bySector: Record<string, VolatilityProfile[]> | null
}

// ─── Filings ─────────────────────────────────────────────────

export type FormType = '10-K' | '10-Q' | '8-K' | 'S-1' | 'S-3' | '424B5' | 'DEF 14A' | '13D' | '13G'

export interface Filing {
  accessionNumber: string
  symbol: string
  company: string
  sector: Sector | null
  formType: FormType
  filedAt: string
  periodEnd: string | null
  description: string
  /** Landing page for the filing (EDGAR) */
  edgarUrl: string
  /** Primary document, when known */
  documentUrl: string | null
}

export interface FilingSearchParams {
  symbol?: string
  sector?: string
  formTypes?: FormType[]
  limit?: number
}

// ─── Investment ideas (tracker) ──────────────────────────────

export type IdeaStatus = 'watching' | 'active' | 'closed'

export interface InvestmentIdea {
  id: string
  createdAt: string
  updatedAt: string
  title: string
  thesis: string
  status: IdeaStatus
  symbols: string[]
  tags: string[]
  notes: string
  /** Where the idea came from */
  source: 'manual' | 'opus-flag' | 'screener'
}

/** Live enrichment for a tracked symbol — price, earnings, latest filings. */
export interface IdeaSymbolContext {
  symbol: string
  name: string | null
  price: number | null
  changePercent: number | null
  nextEarningsDate: string | null
  daysUntilEarnings: number | null
  latestFilings: Filing[]
}

// ─── Opus research assistant ─────────────────────────────────

export type ResearchTaskType =
  | 'analyze-filing'
  | 'analyze-security'
  | 'scan-offerings'
  | 'flag-thesis'
  | 'match-position'

export interface OpusAnalysisRequest {
  task: ResearchTaskType
  /** Free-form question, thesis text, or instructions */
  prompt?: string
  symbol?: string
  symbols?: string[]
  sector?: string
  accessionNumber?: string
  /** For flag-thesis: minimum short-term vol divergence to flag on */
  minDivergence?: number
  /** For match-position: e.g. "short-term bonds", "convertible debt" */
  positionType?: string
  includeOptionsChain?: boolean
  includeFundamentals?: boolean
}

export interface FlaggedSecurity {
  symbol: string
  name: string
  sector: string
  /** 0–100 relevance/conviction score */
  score: number
  reason: string
  volDivergence: number | null
  nextEarningsDate: string | null
  relatedFilings: Filing[]
}

export interface OfferingTerm {
  symbol: string
  company: string
  instrument: string // e.g. "Convertible Senior Notes", "Series A Preferred"
  summary: string
  keyTerms: { label: string; value: string }[]
  sourceFiling: Filing | null
}

export interface OpusAnalysisResponse {
  id: string
  task: ResearchTaskType
  model: string
  createdAt: string
  /** Main analysis narrative (markdown-lite: paragraphs and "- " bullets) */
  summary: string
  keyFindings: string[]
  flagged: FlaggedSecurity[]
  offerings: OfferingTerm[]
  citations: { label: string; url: string }[]
  /** True when generated by the local mock rather than the live Opus backend */
  isMock: boolean
}

// ─── Marker Lab (unsupervised marker-influence analysis) ─────

export type MarkerGroup = 'options' | 'sentiment' | 'fundamental' | 'macro' | 'factor'

export interface MarkerDef {
  id: string
  label: string
  group: MarkerGroup
  description: string
}

/** Weekly trading markers evaluated for influence on forward returns. */
export const MARKERS: readonly MarkerDef[] = [
  { id: 'iv-divergence', label: 'Options: IV−RV divergence', group: 'options', description: '30d implied minus realized vol — options rich/cheap vs recent movement' },
  { id: 'options-skew', label: 'Options: put-call skew', group: 'options', description: '25-delta put IV minus call IV — downside hedging demand' },
  { id: 'options-volume', label: 'Options: volume vs 20d avg', group: 'options', description: 'Chain-wide contract volume relative to its trailing average' },
  { id: 'x-mentions', label: 'X mentions trend', group: 'sentiment', description: 'Week-over-week change in X (Twitter) cashtag mention counts' },
  { id: 'x-sentiment', label: 'X sentiment score', group: 'sentiment', description: 'Net positive/negative tone of trending X posts on the name' },
  { id: 'news-tone', label: 'Company news tone', group: 'sentiment', description: 'Aggregated tone of company press and news announcements' },
  { id: 'earnings-surprise', label: 'Earnings & statement surprise', group: 'fundamental', description: 'Standardized surprise from earnings and statement announcements' },
  { id: 'fed-rate', label: 'Fed funds rate change', group: 'macro', description: 'Weekly change in the effective fed funds target' },
  { id: 'fed-shock', label: 'Fed announcement surprise', group: 'macro', description: 'FOMC statement/press-conference surprise vs market pricing' },
  { id: 'jobs-surprise', label: 'Jobs report surprise', group: 'macro', description: 'Nonfarm payrolls print vs consensus, standardized' },
  { id: 'usd-fx', label: 'USD / forex movement', group: 'macro', description: 'Broad dollar index weekly move' },
  { id: 'ff-smb', label: 'Fama–French SMB (size)', group: 'factor', description: 'Small-minus-big factor return, prior week' },
  { id: 'ff-hml', label: 'Fama–French HML (value)', group: 'factor', description: 'High-minus-low book/market factor return, prior week' },
] as const

export interface MarkerInfluence {
  marker: string
  label: string
  group: MarkerGroup
  /** Pearson correlation of the (lagged) marker with forward returns */
  correlation: number
  /** Share of total absolute correlation, 0–100 (sums to ~100 across markers) */
  influence: number
  direction: 'positive' | 'negative'
  tStat: number
}

export interface PcaComponentSummary {
  component: number
  /** Fraction of total marker variance explained, 0–1 */
  varianceExplained: number
  cumulativeVariance: number
  topLoadings: { marker: string; label: string; loading: number }[]
  interpretation: string
}

export interface SectorFactorLoadings {
  sector: Sector
  /** Annualized intercept, % */
  alphaAnnualPct: number
  marketBeta: number
  smb: number
  hml: number
  r2: number
}

export type SizeBucket = 'Mega' | 'Large' | 'Mid' | 'Small'

export interface SizePlacement {
  symbol: string
  name: string
  sector: Sector
  /** $B */
  marketCap: number
  rankInSector: number
  sectorCount: number
  /** 0–100, 100 = largest in sector */
  percentile: number
  bucket: SizeBucket
}

export interface SymbolSignal {
  symbol: string
  name: string
  sector: Sector
  /** Correlation-weighted composite of current marker readings (z-units) */
  signalZ: number
  expectedAnnualReturnPct: number
  annualVolPct: number
}

export interface MarkerLabParams {
  sector?: Sector | null
  /** Forward-return horizon the markers are tested against */
  horizonWeeks?: number
}

export interface MarkerLabResult {
  asOf: string
  scope: string
  sampleWeeks: number
  symbolsAnalyzed: number
  observations: number
  horizonWeeks: number
  influences: MarkerInfluence[]
  components: PcaComponentSummary[]
  sectorFactors: SectorFactorLoadings[]
  sizePlacements: SizePlacement[]
  symbolSignals: SymbolSignal[]
}

// ─── Macro conditions (Marker Lab context panel) ─────────────

export type MacroStanceLabel = 'Dovish' | 'Leans dovish' | 'Neutral' | 'Leans hawkish' | 'Hawkish'
export type JobsSentiment = 'strong' | 'inline' | 'soft'

export interface FedConditions {
  /** Target range, % [lower, upper] */
  targetRangePct: [number, number]
  effectiveRatePct: number
  lastMove: 'hike' | 'cut' | 'hold'
  lastMoveBps: number
  lastDecisionDate: string
  /** −1 (dovish) … +1 (hawkish), read from statement/press-conference tone */
  stance: number
  stanceLabel: MacroStanceLabel
  nextMeetingDate: string
  note: string
}

export interface JobsConditions {
  /** Latest nonfarm payrolls print, thousands */
  nonfarmPayrollsK: number
  consensusK: number
  surpriseK: number
  unemploymentRatePct: number
  avgHourlyEarningsYoYPct: number
  sentiment: JobsSentiment
  lastReportDate: string
  nextReportDate: string
  note: string
}

export interface FxRateReading {
  /** Quote symbol, e.g. EURUSD=X */
  pair: string
  /** Display label, e.g. EUR/USD */
  label: string
  price: number
  changePercent: number
  /** Whether a rise in this quote means a stronger USD */
  usdUpWhenHigher: boolean
  live: boolean
}

export interface MacroMarketReading {
  label: string
  value: number | null
  changePercent: number | null
  unit: string
  live: boolean
}

export interface SuggestedIndicator {
  id: string
  label: string
  why: string
}

export interface MacroConditions {
  asOf: string
  /** True when fed/jobs figures are curated samples rather than a live feed */
  fedJobsCurated: boolean
  fed: FedConditions
  jobs: JobsConditions
  dollarIndex: MacroMarketReading | null
  fx: FxRateReading[]
  /** Extra live context: 10Y yield, VIX */
  markets: MacroMarketReading[]
  suggested: SuggestedIndicator[]
}

// ─── Convertible / preferred parity scanner ──────────────────

export type ConvertibleKind = 'Convertible Note' | 'Convertible Preferred'

export type ConvertVerdict = 'undervalued' | 'fair' | 'rich'

/**
 * One convertible instrument with its embedded option priced against the
 * listed options chain. All *Pct valuation fields are % of par.
 */
export interface ConvertibleIssue {
  id: string
  symbol: string
  company: string
  sector: Sector
  kind: ConvertibleKind
  /** Coupon (notes) or dividend rate (preferred), % */
  couponPct: number
  parValue: number
  /** null = perpetual preferred */
  maturity: string | null
  /** Pricing tenor in years (to maturity, or to call for perpetuals) */
  tenorYears: number
  conversionPrice: number
  /** Shares per unit of par */
  conversionRatio: number
  stockPrice: number
  // Redemption metrics
  callDate: string
  callPricePct: number
  /** Soft-call stock-price trigger as % of conversion price (notes), null = hard call */
  softCallTriggerPct: number | null
  creditSpreadBps: number
  // Valuation
  marketPricePct: number
  parityPct: number
  conversionPremiumPct: number
  bondFloorPct: number
  /** Market price minus bond floor — what the market charges for the embedded option */
  embeddedOptionPct: number
  /** Bond floor + conversion option valued at the listed chain's IV */
  modelValuePct: number
  /** Vol that reprices the convert's market price — the embedded option's implied vol */
  convertImpliedVol: number | null
  /** Listed option IV interpolated to the conversion tenor */
  listedIv: number
  /** listedIv − convertImpliedVol; positive = embedded option priced on cheaper vol */
  volGapPts: number | null
  /** (model − market) / market × 100 */
  cheapnessPct: number
  verdict: ConvertVerdict
  sourceFiling: Filing | null
}

export interface ConvertibleScanParams {
  sector?: Sector | null
  kind?: 'all' | 'note' | 'preferred'
}

export interface ConvertibleScanResult {
  asOf: string
  riskFreeRatePct: number
  issues: ConvertibleIssue[]
}

// ─── Monte Carlo strategy lab ────────────────────────────────

export type McStrategy = 'marker-tilt' | 'convert-parity'

export interface McConfig {
  strategy: McStrategy
  sector?: Sector | null
  paths: number
  horizonDays: number
  notional: number
  seed: number
  /** Multiplier on every volatility input (stress) */
  volMultiplier: number
  /** Additive annualized drift shift, % */
  driftAdjustPct: number
}

export interface McStats {
  meanPnl: number
  medianPnl: number
  stdevPnl: number
  probProfit: number
  p5: number
  p95: number
  var95: number
  cvar95: number
  best: number
  worst: number
  sharpe: number
}

export interface McHistogramBin {
  start: number
  end: number
  count: number
}

export interface McSensitivity {
  param: string
  label: string
  /** Mean P&L with the parameter at −20% / +20% */
  low: number
  high: number
}

export interface McPosition {
  symbol: string
  weightPct: number
  note: string
}

export interface McResult {
  config: McConfig
  strategyLabel: string
  positions: McPosition[]
  stats: McStats
  histogram: McHistogramBin[]
  /** Fitted normal curve scaled to histogram counts */
  normalOverlay: { x: number; y: number }[]
  /** A few dozen simulated portfolio-value paths for display */
  samplePaths: number[][]
  sensitivities: McSensitivity[]
  /** True when strategy inputs came from mock data */
  basisMock: boolean
}
